import { useState } from 'react'
import { TriangleAlert, RotateCcw, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { TokenDialog } from '@/components/projects/connectors'
import { CONNECTOR_LABELS, supportsMultipleInstances } from '@/components/projects/connectors/connectorConstants'
import { useReconnectConnector } from '@/hooks/useReconnectConnector'
import type { ConnectorType } from '@/types'

interface ConnectorUnavailableBannerProps {
  projectId: string
  connectorType: string
  message: string
  variant?: 'expired' | 'missing'
  onReconnected?: (connectorType: ConnectorType) => void
}

function isKnownConnectorType(value: string): value is ConnectorType {
  return value in CONNECTOR_LABELS
}

export function ConnectorUnavailableBanner({ projectId, connectorType, message, variant = 'expired', onReconnected }: Readonly<ConnectorUnavailableBannerProps>) {
  const [dismissed, setDismissed] = useState(false)

  const knownType = isKnownConnectorType(connectorType) ? connectorType : null
  const label = knownType ? CONNECTOR_LABELS[knownType] : connectorType
  const title = variant === 'missing' ? `${label} — Not connected` : `${label} — Connector token expired`
  const canReconnect = variant === 'expired' && knownType !== null && !supportsMultipleInstances(knownType)

  const { reconnect, isReconnecting, tokenDialog } = useReconnectConnector({
    projectId,
    onSuccess: (type) => {
      setDismissed(true)
      if (onReconnected) onReconnected(type)
    },
  })

  if (dismissed) return null

  return (
    <div className="py-3" role="alert" data-testid={`connector-${variant}-banner`}>
      <div className="mx-auto rounded-lg border border-danger-foreground bg-danger px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex h-5 w-5 shrink-0 items-center justify-center mt-0.5">
            <TriangleAlert className="h-4 w-4 text-danger-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-danger-foreground">{title}</p>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0 text-danger-foreground"
                onClick={() => setDismissed(true)}
                aria-label={`Dismiss ${label} warning`}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="mt-1 text-sm text-foreground">{message}</p>
            {canReconnect && (
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                disabled={isReconnecting}
                onClick={() => reconnect(knownType)}
              >
                <RotateCcw className="mr-1 h-3.5 w-3.5" />
                Reconnect {label}
              </Button>
            )}
          </div>
        </div>
      </div>

      {canReconnect && (
        <TokenDialog
          projectId={tokenDialog.projectId}
          connectorType={tokenDialog.connectorType}
          open={tokenDialog.open}
          onOpenChange={tokenDialog.onOpenChange}
          onSave={tokenDialog.onSave}
          isSaving={tokenDialog.isSaving}
          inlineError={tokenDialog.inlineError}
        />
      )}
    </div>
  )
}
